import React from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Button, { ILink } from 'tbk-components/src/components/Button';
import AnimatedArcs from '../../common/AnimatedArcs';
import VideoModal, { IVideoModalProps } from '../../common/VideoModal';
import logo from '../../../assets/images/logo-colour.svg';
export interface Content {
	/**
	 * Content Link
	 */
	link?: IImage;
	/**
	 * Content Logo
	 */
	logo?: IImage;
	/**
	 * Content Year
	 */
	year?: string;
	/**
	 * Content Quarter
	 */
	quarter?: string;
	/**
	 * Content Label
	 */
	label?: string;
}
export interface File {
	/**
	 * File Link
	 */
	link?: IImage;
	/**
	 * File Image
	 */
	image?: IImage;
	/**
	 * File Label
	 */
	label?: string;
}
export interface Video {
	/**
	 * Video Settings
	 */
	video?: IVideoModalProps;
	/**
	 * Video Label
	 */
	label?: string;
}

export interface ILatestEarningsReportProps extends IBaseElementProps {
	/**
	 * Latest Earnings Report Heading
	 */
	heading?: string;
	/**
	 * Latest Earnings Report Content
	 */
	content?: Content;
	/**
	 * Latest Earnings Report File One
	 */
	fileOne?: File;
	/**
	 * Latest Earnings Report File Two
	 */
	fileTwo?: File;
	/**
	 * Latest Earnings Report File Three
	 */
	fileThree?: File;
	/**
	 * Latest Earnings Report Video
	 */
	video?: Video;
	/**
	 * Latest Earnings Report CTA
	 */
	cta?: ILink;
	/**
	 * Enable Line Animations
	 */
	enableLineAnimations?: boolean;
	/**
	 * Line Animation Delay in Milliseconds
	 */
	lineAnimationDelay?: number;
	/**
	 * Line Animation Offset in Pixels
	 */
	lineAnimationOffset?: number;
}

const defaultCalianLogo = {
	src: logo,
	alt: 'Calian Group.',
	width: 180,
	height: 65,
} as IImage;
/**
 * Latest Earnings Report
 * @block
 * @icon money-alt
 */
const LatestEarningsReport: React.FC<ILatestEarningsReportProps> = ({
	heading,
	content,
	fileOne,
	fileTwo,
	fileThree,
	video,
	cta,
	id,
	className,
	classNames = [],
	enableLineAnimations = true,
	lineAnimationDelay = 1000,
	lineAnimationOffset = 250,
}) => (
	<div
		id={id}
		className={className || cn(
			'calian-latest-earnings-report',
			...classNames,
			enableLineAnimations ? '' : 'disable-line-animations',
		)}
	>
		<div className="calian-latest-earnings-report__container">
			<div className="calian-latest-earnings-report__wrapper">
				{heading && (
					<h2 className="calian-latest-earnings-report__heading">
						{heading}
					</h2>
				)}
				{content?.link?.src && content?.label && (
					<div className="calian-latest-earnings-report__items">
						<div className="calian-latest-earnings-report__item calian-latest-earnings-report__item--content">
							<a
								href={content.link.src}
								className="calian-latest-earnings-report__content"
								target="_blank"
								rel="noreferrer noopener"
							>
								<div className="calian-latest-earnings-report__document-container">
									<div className="calian-latest-earnings-report__document-wrapper">
										<div className="calian-latest-earnings-report__document">
											<Image
												{...(content?.logo?.src
													? content.logo
													: defaultCalianLogo)}
												className="calian-latest-earnings-report__logo img-fluid"
											/>

											{content.quarter && (
												<div className="calian-latest-earnings-report__quarter">
													{content.quarter}
												</div>
											)}
											{content.year && (
												<div className="calian-latest-earnings-report__year">
													{content.year}
												</div>
											)}
										</div>
									</div>
								</div>
								<span className="calian-latest-earnings-report__label calian-latest-earnings-report__label--large">
									{content.label}
								</span>
							</a>
						</div>
						{fileOne?.link?.src && fileOne?.label && (
							<div className="calian-latest-earnings-report__item calian-latest-earnings-report__item--file">
								<a
									href={fileOne?.link?.src}
									className="calian-latest-earnings-report__file"
									target="_blank"
									rel="noreferrer noopener"
								>
									<span className="calian-latest-earnings-report__label">
										{fileOne.label}
									</span>
									{fileOne?.image?.src && (
										<div className="calian-latest-earnings-report__image-container">
											<Image
												{...fileOne.image}
												className="calian-latest-earnings-report__image img-fluid"
											/>
										</div>
									)}
								</a>
							</div>
						)}
						{fileTwo?.link?.src && fileTwo?.label && (
							<div className="calian-latest-earnings-report__item calian-latest-earnings-report__item--file">
								<a
									href={fileTwo?.link?.src}
									className="calian-latest-earnings-report__file"
									target="_blank"
									rel="noreferrer noopener"
								>
									<span className="calian-latest-earnings-report__label">
										{fileTwo?.label}
									</span>
									{fileTwo?.image?.src && (
										<div className="calian-latest-earnings-report__image-container">
											<Image
												{...fileTwo.image}
												className="calian-latest-earnings-report__image img-fluid"
											/>
										</div>
									)}
								</a>
							</div>
						)}
						{fileThree?.link?.src && fileThree?.label && (
							<div className="calian-latest-earnings-report__item calian-latest-earnings-report__item--file">
								<a
									href={fileThree?.link?.src}
									className="calian-latest-earnings-report__file"
									target="_blank"
									rel="noreferrer noopener"
								>
									<span className="calian-latest-earnings-report__label">
										{fileThree?.label}
									</span>
									{fileTwo?.image?.src && (
										<div className="calian-latest-earnings-report__image-container">
											<Image
												{...fileThree.image}
												className="calian-latest-earnings-report__image img-fluid"
											/>
										</div>
									)}
								</a>
							</div>
						)}
						{(video?.video?.oembed || video?.video?.url?.src) &&
							video?.video?.poster &&
							video?.label && (
								<div className="calian-latest-earnings-report__item calian-latest-earnings-report__item--video">
									<div className="calian-latest-earnings-report__video-container">
										<p className="calian-latest-earnings-report__label">
											{video.label}
										</p>
										<div className="calian-latest-earnings-report__video">
											{video.video && (
												<VideoModal {...video.video} />
											)}
										</div>
									</div>
								</div>
							)}
						{cta?.label && (
							<div className="calian-latest-earnings-report__item calian-latest-earnings-report__item--cta">
								<Button
									{...cta}
									className="calian-latest-earnings-report__cta btn btn-light"
								/>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
		{enableLineAnimations && (
			<AnimatedArcs
				arcsStyle={'Component - Two Lines'}
				arcsPosition="Right"
				arcOneColor="var(--bs-white)"
				arcOneStrokeWidth={4}
				arcTwoColor="var(--bs-primary)"
				arcTwoStrokeWidth={3}
				animationDelay={lineAnimationDelay}
				animationOffset={lineAnimationOffset}
			/>
		)}
	</div>
);

export default LatestEarningsReport;
